// sim/ecosystemPressure.ts — wildlife reshapes colony behaviour.
//
// The wildlife layer (wildlife.ts) hunts, raids, and migrates on its own. This
// system closes the loop the other way: it senses the wildlife crowding each
// colony's nest, classifies it into three PRESSURES, and feeds them to the colony
// AI so the colony ADAPTS automatically — no player input, all emergent:
//
//   Predation (Spider / Frog)   → more soldiers + foragers pulled in (route change)
//   Aerial    (Bird / Dragonfly)→ Flyer production suppressed (they get picked off)
//   Theft     (Rat)             → the larder is robbed → harder foraging + defence
//
// The colony's RESPONSE is driven through the AI's existing seams: a small, capped
// need bias (Defense/Food up, Expansion down) plus two intent booleans the AI reads
// (foragePulledIn / flyersSuppressed). Theft is actuated here directly (steal food).
//
// The scan senses the NEST by default; when a hive-node anchor reader is attached
// (attachAnchors), it ALSO covers each founded hive node and combines per-channel by
// MAX, so a threat camped on a remote node is felt without double-counting one that
// straddles two anchors. No reader ⇒ nest-only, byte-identical to before.
//
// DETERMINISM: pure integer SoA, fixed colony order, NO RNG, allocation-free. Runs
// on a calm cadence (scanInterval). Folds into the ecosystem state hash. OPTIONAL —
// a world without it (no config.pressure) is byte-identical.

import { KIND, NEED, SPECIES } from '../types.ts';
import type { System, Tick, ColonyId, Need, EcosystemPressureConfig } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import { NIL } from '../types.ts';
import type { ColonySystem } from './colony.ts';
import type { WildlifeSystem } from './wildlife.ts';
import type { EcosystemPressureRead } from './colonyAI.ts';

/**
 * OPTIONAL hive-node anchor reader (late-bound via `attachAnchors`). When wired, the
 * pressure scan covers not just the nest but each of the colony's founded hive nodes,
 * so a threat camped on a remote node is FELT (it no longer reads as zero). The
 * `ColonyAISystem` satisfies this interface directly (its `hiveCountAt`/`hiveXAt`/
 * `hiveYAt`), so wiring is a one-liner with no adapter. The NEST itself is always
 * sourced from the colony system, so anchor 0 is implicit — this reader supplies only
 * the EXTRA hive-node anchors. Absent ⇒ the scan covers the nest only, byte-identical
 * to before this layer existed.
 */
export interface HiveAnchorsRead {
  /** How many hive nodes colony `cid` has founded (0 ⇒ nest-only). */
  hiveCountAt(cid: ColonyId): number;
  /** X of colony `cid`'s k-th hive node (0 ≤ k < hiveCountAt). */
  hiveXAt(cid: ColonyId, k: number): number;
  /** Y of colony `cid`'s k-th hive node. */
  hiveYAt(cid: ColonyId, k: number): number;
}

/** Pressure channels. */
const P_NONE = 0;
const P_PRED = 1;
const P_AERIAL = 2;
const P_THEFT = 3;

/** Species → pressure channel (indexed by SPECIES.*). */
const SPECIES_PRESSURE: readonly number[] = (() => {
  const m: number[] = [P_NONE, P_NONE, P_NONE, P_NONE, P_NONE, P_NONE];
  m[SPECIES.Spider] = P_PRED;
  m[SPECIES.Frog] = P_PRED;
  m[SPECIES.Bird] = P_AERIAL;
  m[SPECIES.Dragonfly] = P_AERIAL;
  m[SPECIES.Rat] = P_THEFT;
  m[SPECIES.Beetle] = P_NONE; // herbivore — no pressure
  return m;
})();

/**
 * A live apex BOSS (KIND.Apex) near a nest reads as heavy PREDATION pressure: it
 * hunts members directly, so the colony must muster soldiers + avoid expanding into
 * its path. Bosses are RARE and capped at one alive, so this multiplier sharpens the
 * channel without ever flooding it.
 */
export const APEX_PRESSURE_WEIGHT = 4;

export class EcosystemPressureSystem implements System, EcosystemPressureRead {
  readonly name = 'ecosystem-pressure';

  private readonly colonies: ColonySystem;
  private readonly wildlife: WildlifeSystem;
  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly config: EcosystemPressureConfig;
  /**
   * Hive-node anchors (OPTIONAL, late-bound via `attachAnchors`). When wired, the scan
   * combines the nest box with each hive-node box (per-channel MAX, see `sensePressure`).
   * Absent ⇒ nest-only scan, byte-identical to before this layer.
   */
  private anchors: HiveAnchorsRead | undefined;

  // --- Per-colony pressure (proximity-weighted integer counts). ---
  private readonly predation: Int32Array;
  private readonly aerial: Int32Array;
  private readonly theft: Int32Array;
  /** Derived intent flags (1/0), recomputed each scan so the AI reads cheap booleans. */
  private readonly foragePull: Uint8Array;
  private readonly flyerSuppress: Uint8Array;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  scansTotal = 0;
  foodStolenTotal = 0;

  constructor(
    colonies: ColonySystem,
    wildlife: WildlifeSystem,
    entities: EntitySystem,
    grid: WorldGrid,
    config: EcosystemPressureConfig,
  ) {
    this.colonies = colonies;
    this.wildlife = wildlife;
    this.entities = entities;
    this.grid = grid;
    this.config = config;
    const n = colonies.capacity;
    this.predation = new Int32Array(n);
    this.aerial = new Int32Array(n);
    this.theft = new Int32Array(n);
    this.foragePull = new Uint8Array(n);
    this.flyerSuppress = new Uint8Array(n);
  }

  /**
   * Late-bind the hive-node anchor reader (mirrors `ColonyAISystem.attachPressure`).
   * Once wired, the scan senses wildlife around the nest AND each founded hive node,
   * taking the per-channel MAX across anchors. Not wiring it ⇒ nest-only, byte-identical.
   */
  attachAnchors(anchors: HiveAnchorsRead): void {
    this.anchors = anchors;
  }

  // --- Per-tick: recompute pressure on the scan cadence ----------------------

  update(tick: Tick): void {
    if (tick % this.config.scanInterval !== 0) return;
    const colonies = this.colonies;
    const count = colonies.count;
    for (let cid = 0; cid < count; cid++) {
      // sensePressure writes the three accumulators into scratch fields (no alloc).
      this.scanPred = 0;
      this.scanAerial = 0;
      this.scanTheft = 0;
      this.scanTheftCount = 0;
      if (colonies.isAlive(cid)) this.sensePressure(cid);
      const pred = this.scanPred;
      const aer = this.scanAerial;
      const theft = this.scanTheft;
      this.predation[cid] = pred;
      this.aerial[cid] = aer;
      this.theft[cid] = theft;
      this.foragePull[cid] = pred >= this.config.forageSquelch ? 1 : 0;
      this.flyerSuppress[cid] = aer >= this.config.aerialSquelch ? 1 : 0;

      // Theft is actuated here: a Rat presence robs the larder. Each nearby Rat takes
      // its OWN bite — the per-scan cap scales with the raiding party (theftDrain ×
      // rats), so a swarm meaningfully robs a big larder while a lone rat only nips.
      // Still bounded by the sensed theft magnitude (and by the store, in stealFood).
      if (theft > 0) {
        let cap = this.config.theftDrain * this.scanTheftCount;
        // SIZE-RELATIVE theft (opt-in): a fat larder draws proportionally bigger raids —
        // add a per-mille slice of the STORED food to the cap. This only RAISES the cap
        // toward the sensed-theft ceiling (want = min(theft, cap)); it never exceeds the
        // sensed magnitude, and stealFood still bounds it by the store. Omitted / 0 ⇒ the
        // cap is unchanged and `foodAt` isn't even read ⇒ byte-identical.
        const permille = this.config.theftLarderPermille ?? 0;
        if (permille > 0) cap += Math.floor((colonies.foodAt(cid) * permille) / 1000);
        const want = theft < cap ? theft : cap;
        this.foodStolenTotal += colonies.stealFood(cid, want);
      }
    }
    this.scansTotal++;
  }

  // Scratch outputs for the scan (avoids allocating a tuple per colony/anchor).
  // scan* hold the COMBINED result across a colony's anchors; box* hold one box.
  private scanPred = 0;
  private scanAerial = 0;
  private scanTheft = 0;
  private scanTheftCount = 0; // number of raiding Rats (drives the per-rat theft bite)
  private boxPred = 0;
  private boxAerial = 0;
  private boxTheft = 0;
  private boxTheftCount = 0;

  /**
   * Sense wildlife pressure for colony `cid` across all of its ANCHORS — the nest plus
   * each founded hive node (when an anchor reader is attached) — and combine into the
   * scan scratch fields.
   *
   * COMBINE RULE: per-channel MAX across anchors. Each anchor box is scanned
   * independently (its own proximity-weighted sum); the colony's pressure in a channel
   * is the LARGEST single-anchor box total for that channel. MAX (not sum) is the
   * deliberate combine so a threat sitting near TWO anchors is never double-counted:
   * it contributes its weight to each overlapping box's independent total, and MAX
   * keeps only one of them — the same number a single nest scan would yield if that
   * anchor were the nest. theftCount rides with whichever anchor wins the theft MAX,
   * so the per-rat theft bite stays consistent with the sensed theft magnitude.
   *
   * Anchors are visited in a fixed order (nest, then hive node 0..k-1) and MAX is
   * order-independent, so the result is deterministic. No allocation; reuses scratch.
   * Nest-only (no reader, or a colony with zero hive nodes) ⇒ byte-identical to before.
   */
  private sensePressure(cid: ColonyId): void {
    // Anchor 0: the nest (always present).
    this.senseBox(cid, this.colonies.homeXAt(cid), this.colonies.homeYAt(cid));
    let pred = this.boxPred;
    let aer = this.boxAerial;
    let theft = this.boxTheft;
    let theftCount = this.boxTheftCount;

    // Anchors 1..k: founded hive nodes (only when a reader is attached).
    const anchors = this.anchors;
    if (anchors !== undefined) {
      const nodes = anchors.hiveCountAt(cid);
      for (let k = 0; k < nodes; k++) {
        this.senseBox(cid, anchors.hiveXAt(cid, k), anchors.hiveYAt(cid, k));
        if (this.boxPred > pred) pred = this.boxPred;
        if (this.boxAerial > aer) aer = this.boxAerial;
        // theftCount pairs with the anchor that wins the theft channel (strict >, so
        // the FIRST anchor reaching a given max keeps its count — deterministic tie-break).
        if (this.boxTheft > theft) { theft = this.boxTheft; theftCount = this.boxTheftCount; }
      }
    }

    this.scanPred = pred;
    this.scanAerial = aer;
    this.scanTheft = theft;
    this.scanTheftCount = theftCount;
  }

  /**
   * Accumulate proximity-weighted wildlife pressure within `pressureRadius` of a single
   * anchor `(ax, ay)` into the box scratch fields. Mirrors colonyAI.countThreats'
   * grid-box walk; closer wildlife weighs more (w = radius − chebyshev + 1).
   */
  private senseBox(_cid: ColonyId, ax: number, ay: number): void {
    const grid = this.grid;
    const e = this.entities;
    const wl = this.wildlife;
    const r = this.config.pressureRadius;
    const minX = ax - r < 0 ? 0 : ax - r;
    const minY = ay - r < 0 ? 0 : ay - r;
    const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
    const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;
    let pred = 0;
    let aer = 0;
    let theft = 0;
    let theftCount = 0;
    for (let ty = minY; ty <= maxY; ty++) {
      const ady = ty - ay < 0 ? ay - ty : ty - ay;
      for (let tx = minX; tx <= maxX; tx++) {
        const adx = tx - ax < 0 ? ax - tx : tx - ax;
        const dist = adx > ady ? adx : ady;
        const w = r - dist + 1; // proximity weight, ≥ 1 inside the box
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          const kind = e.kind[c] as number;
          // A live apex BOSS looms as heavy predation pressure — it belongs to no
          // species channel; it's a direct threat to colony members.
          if (kind === KIND.Apex) {
            if ((e.hp[c] as number) > 0) pred += w * APEX_PRESSURE_WEIGHT;
            continue;
          }
          if (kind !== KIND.Wildlife) continue;
          if ((e.hp[c] as number) <= 0) continue;
          const sp = wl.species[c] as number;
          const channel = SPECIES_PRESSURE[sp] as number;
          if (channel === P_PRED) pred += w;
          else if (channel === P_AERIAL) aer += w;
          else if (channel === P_THEFT) { theft += w; theftCount++; }
        }
      }
    }
    this.boxPred = pred;
    this.boxAerial = aer;
    this.boxTheft = theft;
    this.boxTheftCount = theftCount;
  }

  // --- EcosystemPressureRead (consumed by the colony AI) ---------------------

  biasFor(cid: ColonyId, need: Need): number {
    switch (need) {
      // Predators AND raiders both call for soldiers.
      case NEED.Defense: return this.scaled((this.predation[cid] as number) + (this.theft[cid] as number));
      // A robbed larder reads as hunger → forage harder.
      case NEED.Food: return this.scaled(this.theft[cid] as number);
      // Don't push out into danger (birds eat scouts, spiders ambush expanders).
      case NEED.Expansion: return -this.scaled((this.predation[cid] as number) + (this.aerial[cid] as number));
      default: return 0;
    }
  }

  /** raw pressure → capped integer bias. */
  private scaled(raw: number): number {
    const s = Math.floor(raw / this.config.biasDivisor);
    const cap = this.config.biasCap;
    return s > cap ? cap : s;
  }

  foragePulledIn(cid: ColonyId): boolean {
    return this.foragePull[cid] === 1;
  }
  flyersSuppressed(cid: ColonyId): boolean {
    return this.flyerSuppress[cid] === 1;
  }

  // --- Accessors (hash / CLI / tests / render) -------------------------------

  predationAt(cid: ColonyId): number { return this.predation[cid] as number; }
  aerialAt(cid: ColonyId): number { return this.aerial[cid] as number; }
  theftAt(cid: ColonyId): number { return this.theft[cid] as number; }
}
